import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ModalBody, ModalWrapper } from '@zooplus/ui-components';

import RetryPaymentCreditCardModalError from '../RetryPaymentCreditCardModalError/RetryPaymentCreditCardModalError';

import AdyenCreditCardForm, {
  AdyenCreditCardFormRef,
} from '@components/AdyenCreditCardForm/AdyenCreditCardForm';
import ModalButtons from '@components/ModalButtons/ModalButtons';
import useSupportedCreditCardBrands from '@hooks/useSupportedCreditCardBrands';
import { useTranslation } from '@hooks/useTranslations';
import { useConfig } from '@providers/ConfigProvider';
import { OverlayIds, useOverlays } from '@providers/OverlaysProvider';
import { useRetryState } from '@providers/RetryDataProvider';
import { useThunkDispatch } from '@providers/ThunkDispatchProvider';
import { collectBrowserInfo, initiatePayment } from '@services/retryPaymentService';
import { captureException } from '@services/sentryService';
import { RetryPaymentStatus } from '@types-internal';
import { AdyenCreditCardData } from '@types-internal/AdyenTypes';
import { InitiateRetryPaymentCreditCardRequestData } from '@types-internal/RetryTypes';
import { getOrderDetailsUrl } from '@utils/retryUtils';
import { convertRunningEnvToAdyenEnv } from '@utils/utils';

type RetryPaymentAdyenModalProps = {
  show: boolean;
  onClose: () => void;
  amount: number;
  orderId: string;
  currency: string;
  siteId: number;
  customerId: string;
};

const RetryPaymentAdyenModal: React.FC<RetryPaymentAdyenModalProps> = ({
  show,
  onClose,
  orderId,
  siteId,
  customerId,
  amount,
}) => {
  const { t } = useTranslation();
  const { hideOverlay, showOverlay } = useOverlays();
  const { config } = useConfig();
  const { thunkDispatch } = useThunkDispatch();
  const {
    retryState: { initiatePaymentData, processAuthorisationData },
  } = useRetryState();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [showErrorModal, setShowErrorModal] = useState(false);

  const supportedCreditCardBrands = useSupportedCreditCardBrands(siteId, customerId, amount);
  const creditCardFormHandle = useRef<AdyenCreditCardFormRef>(null);
  const adyenEnvironment = convertRunningEnvToAdyenEnv(config.env);

  const handleCancel = useCallback(() => {
    onClose();
    setIsLoading(false);
    setErrorMessage(undefined);
  }, [onClose]);

  const returnUrl = getOrderDetailsUrl();

  const handleAuthorize = async () => {
    setErrorMessage(undefined);
    try {
      if (!creditCardFormHandle.current) {
        captureException({ message: 'adyen_retry_form_handle_not_found' });
        setShowErrorModal(true);
        return;
      }

      const creditCardData = creditCardFormHandle.current.computeAdyenCreditCardFormData();

      if (!creditCardData) return;

      const adyenCreditCardData: AdyenCreditCardData = {
        brand: creditCardData.creditCardBrand,
        holderName: creditCardData.holderName,
        encryptedCardNumber: creditCardData.encryptedCardNumber,
        encryptedExpiryMonth: creditCardData.encryptedExpiryMonth,
        encryptedExpiryYear: creditCardData.encryptedExpiryYear,
        encryptedSecurityCode: creditCardData.encryptedSecurityCode,
      };

      const payload: InitiateRetryPaymentCreditCardRequestData = {
        orderId,
        customerId,
        returnUrl,
        creditCardDetails: adyenCreditCardData,
        browserInfo: collectBrowserInfo(),
        paymentMethod: 'CREDIT_CARD',
        connector: 'ADYEN',
        originUrl: returnUrl,
      };

      setIsLoading(true);
      thunkDispatch(initiatePayment(payload));
    } catch (error: any) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const handleRetryPaymentStatus = useCallback(
    (retryPaymentStatus: RetryPaymentStatus | undefined, isFailure: boolean) => {
      if (retryPaymentStatus) {
        switch (retryPaymentStatus) {
          case 'SUCCESSFUL':
            onClose();
            window.location.replace(
              window.location.origin + window.location.pathname + '?auth=SUCCESSFUL',
            );
            break;
          case 'UNSUCCESSFUL':
          case 'CANCELLED':
            setErrorMessage(t('checkout.payment.creditcard.error.authorize.retry'));
            setIsLoading(false);
            break;
          case 'PROCESSING':
            onClose();
            window.location.replace(
              window.location.origin + window.location.pathname + '?auth=PENDING',
            );
            break;
          default:
            // Redirect to order overview page
            window.location.replace(window.location.origin + window.location.pathname);
            break;
        }
      } else {
        if (isFailure) {
          setShowErrorModal(true);
        }
      }
    },
    [],
  );

  useEffect(() => {
    if (initiatePaymentData?.isFetching) return;
    if (initiatePaymentData?.actionObject) {
      showOverlay(OverlayIds.ADYEN_CREDIT_CARD_AUTH);
    } else {
      handleRetryPaymentStatus(
        initiatePaymentData?.retryPaymentStatus,
        initiatePaymentData?.isFailure ?? false,
      );
    }
  }, [initiatePaymentData]);

  useEffect(() => {
    if (processAuthorisationData?.isFetching) return;
    if (
      processAuthorisationData?.authorizationResult?.retryPaymentStatus ||
      processAuthorisationData?.isFailure
    ) {
      hideOverlay(OverlayIds.ADYEN_CREDIT_CARD_AUTH);
    }
    handleRetryPaymentStatus(
      processAuthorisationData?.authorizationResult?.retryPaymentStatus,
      processAuthorisationData?.isFailure ?? false,
    );
  }, [processAuthorisationData]);

  const handleFatalError = useCallback(
    (error, hideForm) => {
      setErrorMessage(error);
      if (hideForm) onClose();
      setIsLoading(false);
    },
    [onClose],
  );

  return (
    <>
      <ModalWrapper
        size="md"
        isOpen={show && !showErrorModal}
        onClose={handleCancel}
        heading={t('checkout.payment.by.card')}
        headingId="retry-payment-adyen-card"
        closeBtnAriaLabel="Close"
        data-zta="retry-payment-modal">
        <ModalBody>
          {show && !showErrorModal && (
            <AdyenCreditCardForm
              ref={creditCardFormHandle}
              supportedCreditCardBrands={supportedCreditCardBrands}
              adyenEnvironment={adyenEnvironment}
              errorMessage={errorMessage}
              clientKey={config.adyen.clientKey}
              labelInline={false}
              /* cannot save card during the retry */
              allowVaulting={false}
              onFatalError={handleFatalError}
            />
          )}
        </ModalBody>
        <ModalButtons
          handleCancel={handleCancel}
          handleAuthorize={handleAuthorize}
          isLoading={isLoading}
        />
      </ModalWrapper>
      <RetryPaymentCreditCardModalError show={showErrorModal} connector={'ADYEN'} />
    </>
  );
};

export default RetryPaymentAdyenModal;
